import { adminActionButtonSx } from 'src/theme/adminActionButton';
import AdminModal from 'src/components/AdminModal';
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  CircularProgress,
  Chip
} from '@mui/material';

type ExtraUser = {
  user?: {
    firstName: string;
    lastName: string;
    email: string;
    id?: string | number;
  };
  job?: {
    id?: string | number;
    extra?: boolean;
    rate?: number;
    extraStartTime?: string;
    extraEndTime?: string;
    extraCallType?: string;
  };
  // Fields may also be at top level from getShiftUsers
  id?: string | number;
  extra?: boolean;
  rate?: number;
  extraCallType?: string;
};

type Props = {
  open: boolean;
  handleClose: () => void;
  extras: ExtraUser[];
  quantityToAdd: number;
  onSave: (selectedExtraJobIds: Array<string | number>) => void;
  loading?: boolean;
};

const ShiftExtrasModal: React.FC<Props> = ({
  open,
  handleClose,
  extras,
  quantityToAdd,
  onSave,
  loading = false
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());


  console.log("ShiftExtrasModal - extras:", extras);



  const selectedCount = selectedIds.size;
  const canPickMore = selectedCount < quantityToAdd;
  const canSave = selectedCount > 0;

  const toggleOne = (id: string | number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (!canPickMore) return prev;
        next.add(id);
      }
      return next;
    });
  };

  const handleSaveClick = () => {
    if (!canSave) return;
    const ids = Array.from(selectedIds);
    onSave(ids);
    setSelectedIds(new Set());
    handleClose();
  };

  const handleCancel = () => {
    setSelectedIds(new Set());
    handleClose();
  };

  // Reset selection when modal opens
  React.useEffect(() => {
    if (open) setSelectedIds(new Set());
  }, [open]);

  return (
    <AdminModal open={open} onClose={handleCancel}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '50%',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 3,
          borderRadius: 2,
          maxHeight: '80vh',
          overflowY: 'auto'
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Select Extras to Add Back
        </Typography>

        <Box
          sx={{
            mb: 2,
            p: 1,
            backgroundColor: 'rgba(232, 191, 33, 0.1)',
            borderRadius: 1,
            border: '1px solid #E8BF21'
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            You can select up to <strong>{quantityToAdd}</strong> extra user
            {quantityToAdd === 1 ? '' : 's'} to add back into this shift.
            &nbsp;Selected: <strong>{selectedCount}</strong>
          </Typography>
        </Box>

        {extras.length === 0 ? (
          <Typography variant="body1" sx={{ mt: 2, mb: 2, textAlign: 'center' }}>
            No extras available for this shift.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Typography variant="subtitle2">Select</Typography>
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell align="center">Rate</TableCell>
              
                </TableRow>
              </TableHead>
              <TableBody>
                {extras.map((extra, idx) => {
                  const id = extra?.id || extra?.job?.id || idx;
                  const isSelected = selectedIds.has(id);
                  const disabled = !isSelected && !canPickMore;

                  return (
                    <TableRow
                      key={String(id)}
                      hover
                      onClick={() => !disabled && toggleOne(id)}
                      sx={{
                        cursor: disabled ? 'default' : 'pointer',
                        backgroundColor: isSelected
                          ? 'rgba(232, 191, 33, 0.1)'
                          : 'inherit'
                      }}
                    >
                      <TableCell   onClick={() => !disabled && toggleOne(id)} padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          disabled={disabled}
                          onChange={() => toggleOne(id)}
                        />
                      </TableCell>
                      <TableCell>
                        {extra.user
                          ? `${extra.user.firstName} ${extra.user.lastName}`
                          : 'Unknown'}
                      </TableCell>
                      <TableCell>{extra.user?.email || '-'}</TableCell>
                      <TableCell align="center">
                        ${extra?.rate}
                      </TableCell>
                     
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={handleCancel}
                sx={{
                  width: 100,
                  borderColor: '#E8BF21',
                  borderWidth: 2,
                  color: '#000000',
                  '&:hover': {
                    borderColor: '#E8BF21',
                    borderWidth: 2,
                    backgroundColor: 'none'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  // Skip extras - just close without selecting any
                  onSave([]);
                  setSelectedIds(new Set());
                  handleClose();
                }}
                sx={{
                  width: 150,
                  borderColor: '#000000',
                  borderWidth: 2,
                  color: '#000000',
                  '&:hover': {
                    borderColor: '#252422',
                    borderWidth: 2,
                    backgroundColor: 'rgba(0,0,0,0.04)'
                  }
                }}
              >
                Skip
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleSaveClick}
                disabled={!canSave}
                sx={{
                  width: 150,
                  ...adminActionButtonSx
                }}
              >
                Add Selected
              </Button>
            </>
          )}
        </Box>
      </Box>
    </AdminModal>
  );
};

export default ShiftExtrasModal;
